import {
  Button,
  DuplicateIcon,
  EmptyState,
  ExternalLinkIcon,
  Grid,
  GridColumn,
  GridRow,
  Link,
  LockIcon,
} from '@bp/ui-components';
import { useTranslation } from 'react-i18next';
import { showSuccessToast } from '../../../utils/toast';
import { AppStatusResponse } from '../forms/SubstitionAppSetupForm';

type SubstitionAppSetupOverviewProps = {
  appStatus?: AppStatusResponse;
};

export const SubstitionAppSetupOverview = ({ appStatus }: SubstitionAppSetupOverviewProps) => {
  const { t } = useTranslation();

  const studentId = appStatus?.monitors.find((m) => m.scheduleId === 'schulervertretungsplan')?.publicId;
  const studentLink = studentId ? `https://monitor.vertretungsplan.me/?id=${studentId}` : '';

  const teacherId = appStatus?.monitors.find((m) => m.scheduleId === 'lehrervertretungsplan')?.publicId;
  const teacherLink = teacherId ? `https://monitor.vertretungsplan.me/?id=${teacherId}` : '';

  const adminLink = 'https://monitor.vertretungsplan.me/admin.html';

  async function copyLink(uri: string) {
    try {
      await navigator.clipboard.writeText(uri);
      showSuccessToast(t('common.linkCopied'));
      return Promise.resolve();
    } catch {
      return Promise.reject();
    }
  }

  return (
    <Grid>
      {appStatus ? (
        <>
          <GridRow spacingBottom='s'>
            <GridColumn width={2}>
              <div style={{ color: 'var(--color-grey-light)' }}>{t('substitutions.app.findInCity')}</div>
            </GridColumn>
            <GridColumn width={10}>
              <div>{`${appStatus.country} > ${appStatus.state} > ${appStatus.city}`}</div>
            </GridColumn>
          </GridRow>
          <GridRow spacingTop='s'>
            <GridColumn width={2}>
              <div style={{ color: 'var(--color-grey-light)' }}>{t('substitutions.app.asSchool')}</div>
            </GridColumn>
            <GridColumn width={10}>
              <div>{appStatus.name}</div>
            </GridColumn>
          </GridRow>
          <GridRow spacingTop='l'>
            <GridColumn width={2}>
              <div style={{ color: 'var(--color-grey-light)' }}>{t('substitutions.app.monitorLinks')}</div>
            </GridColumn>
            <GridColumn width={3}>
              <Link
                to={studentLink}
                icon={<ExternalLinkIcon />}
                text={t('substitutions.app.monitorStudent')}
                color='grey-dark'
                underline
                disabled={!studentId}
              />
              <Button
                icon={<DuplicateIcon className='small' />}
                hierarchy='ghost'
                className='mt-2'
                onClick={() => copyLink(studentLink)}
                disabled={!studentId}
              >
                {t('common.copyLink')}
              </Button>
            </GridColumn>
            <GridColumn width={3}>
              <Link
                to={teacherLink}
                icon={<ExternalLinkIcon />}
                text={t('substitutions.app.monitorTeacher')}
                color='grey-dark'
                underline
                disabled={!teacherId}
              />
              <Button
                icon={<DuplicateIcon className='small' />}
                hierarchy='ghost'
                className='mt-2'
                onClick={() => copyLink(teacherLink)}
                disabled={!teacherId}
              >
                {t('common.copyLink')}
              </Button>
            </GridColumn>
            <GridColumn width={3} offset='right'>
              <Link
                to={adminLink}
                icon={<ExternalLinkIcon />}
                text={t('substitutions.app.monitorAdmin')}
                color='grey-dark'
                underline
              />
              <Button
                icon={<DuplicateIcon className='small' />}
                hierarchy='ghost'
                className='mt-2'
                onClick={() => copyLink(adminLink)}
              >
                {t('common.copyLink')}
              </Button>
            </GridColumn>
          </GridRow>
        </>
      ) : (
        <GridRow>
          <EmptyState icon={<LockIcon />} size='small' title={t('substitutions.app.notEnabled')} />
        </GridRow>
      )}
    </Grid>
  );
};
